/**
 * ArchiveDiscoveryRail — a single horizontally-scrollable row of dossiers
 * used above the main filtered grid to surface Featured, Trending, Newest,
 * Recently Updated, Oracle Recommended, and Most Viewed picks.
 *
 * Uses the homepage DossierCard for perfect visual parity.
 */
import React, { useRef } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import DossierCard from "../home/DossierCard";

const ArchiveDiscoveryRail = ({ testid, icon: Icon, title, subtitle, items = [] }) => {
  const scrollerRef = useRef(null);
  if (!items || items.length === 0) return null;

  const scroll = (dir) => {
    const el = scrollerRef.current;
    if (!el) return;
    el.scrollBy({ left: dir * Math.min(680, el.clientWidth * 0.9), behavior: "smooth" });
  };

  return (
    <section data-testid={testid} className="py-8 md:py-10">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="flex items-end justify-between gap-4 mb-5 md:mb-6">
          <div>
            <div className="flex items-center gap-2 mb-2">
              {Icon ? <Icon size={13} className="text-[#D92D20]" /> : null}
              <span className="font-mono text-[10px] uppercase tracking-[0.5em] text-[#D92D20]">
                {title}
              </span>
            </div>
            {subtitle ? (
              <div className="text-[13px] text-[#8A8377] max-w-xl leading-relaxed">
                {subtitle}
              </div>
            ) : null}
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              aria-label={`Scroll ${title} left`}
              data-testid={`${testid}-prev`}
              onClick={() => scroll(-1)}
              className="w-9 h-9 flex items-center justify-center border border-[#3A3630] text-[#A39E93]
                         hover:border-[#D92D20] hover:text-[#E4DFD3] transition-colors"
            >
              <ChevronLeft size={15} />
            </button>
            <button
              type="button"
              aria-label={`Scroll ${title} right`}
              data-testid={`${testid}-next`}
              onClick={() => scroll(1)}
              className="w-9 h-9 flex items-center justify-center border border-[#3A3630] text-[#A39E93]
                         hover:border-[#D92D20] hover:text-[#E4DFD3] transition-colors"
            >
              <ChevronRight size={15} />
            </button>
          </div>
        </div>

        <div
          ref={scrollerRef}
          data-testid={`${testid}-scroller`}
          className="flex gap-4 md:gap-5 overflow-x-auto snap-x snap-mandatory pb-2 -mx-4 md:-mx-8 px-4 md:px-8 no-scrollbar"
          style={{ scrollbarWidth: "none" }}
        >
          {items.map((d) => (
            <div
              key={d.slug}
              data-testid={`${testid}-item-${d.slug}`}
              className="snap-start shrink-0 w-[280px] sm:w-[320px] md:w-[360px]"
            >
              <DossierCard dossier={d} />
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default ArchiveDiscoveryRail;
